<template>
  <div
    :id="file.id"
    class="custom-file-simplified"
    :class="[
      `custom-file-simplified_${fileMarkup}`,
      { 'is-previewable': isPreviewable, 'is-not-openable': isNotOpenable }
    ]"
  >
    <div
      v-if="!file.deleted"
      class="custom-file-simplified__in"
      @click.prevent="showImage()"
    >
      <FilePic
        class="custom-file-simplified__pic"
        :fileName="file.name"
      />
      <div
        v-tooltip="{
          content: file.name,
          theme: 'info-tooltip',
          placement: 'bottom',
          popperClass: 'custom-file__tooltip'
        }"
        class="custom-file-simplified__content"
      >
        <BaseSubHeading
          weight="400"
          size="medium"
          className="custom-file-simplified__cut-name"
          color="monochrome-07"
        >
          {{ file.name }}
        </BaseSubHeading>
        <div class="custom-file-simplified__size__wrapper">
          <BaseSubHeading
            v-if="progress === 0"
            weight="500"
            size="extra-small"
            className="custom-file-simplified__size"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            {{ downloadStatus ? $t(`fileDownloadStatus.${downloadStatus}`) : fileSize }}
          </BaseSubHeading>
          <ProgressLoadingLine
            v-else
            class="custom-file-simplified__progress"
            :progress="progress"
          />
        </div>
      </div>
      <button
        type="button"
        class="custom-file-simplified__download"
        :class="{
          'is-downloading': downloadStatus,
          'is-preparing': downloadStatus === 'preparing'
        }"
        @click.prevent.stop="onFileDownload"
      >
        <Spinner
          v-if="downloadStatus"
          :loading="true"
          className="custom-file__download-spinner"
        >
          <icon
            v-if="downloadStatus === 'preparing'"
            data="@icon/spinner.svg"
            width="var(--icon-24)"
            height="var(--icon-24)"
            color="var(--monochrome02to36)"
            class="spin-animation"
          />
          <icon
            v-else-if="isDark"
            data="@icon/spinner-gradient-dark.svg"
            original
            width="var(--icon-24)"
            height="var(--icon-24)"
            class="spin-animation"
          />
          <icon
            v-else
            data="@icon/spinner-gradient.svg"
            original
            width="var(--icon-24)"
            height="var(--icon-24)"
            class="spin-animation"
          />
        </Spinner>
        <FileDownloadPic v-else />
      </button>
    </div>
    <div
      v-else
      class="custom-file-simplified__in"
    >
      <FilePic
        class="custom-file-simplified__pic"
        :isRemoved="file.deleted"
        :fileName="file.name"
      />
      <div
        type="button"
        class="custom-file-simplified__content"
      >
        <BaseSubHeading
          weight="400"
          size="medium"
          className="custom-file-simplified__cut-name"
          color="monochrome-07"
        >
          {{ file.name }}
        </BaseSubHeading>
        <div class="custom-file-simplified__size__wrapper">
          <BaseSubHeading
            v-if="progress === 0"
            weight="500"
            size="extra-small"
            className="custom-file-simplified__size"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            Deleted by {{ file.deletedBy?.fullName }}
          </BaseSubHeading>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"
import { nextTick } from "vue"

import Avatar from "@/components/Avatar/index.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import FileDownloadPic from "@/components/File/FileDownloadPic.vue"
import FilePic from "@/components/File/FilePic.vue"
import ProgressLoadingLine from "@/components/ProgressBar/ProgressLoadingLine.vue"
import Spinner from "@/components/Spinner/index.vue"

import {
  checkNotOpenable,
  getBrowserPreviewMimeType,
  isPdfConversionPreviewable,
  PDF_PREVIEW_MIME_TYPE
} from "@/helpers/fileType"
import formatBytes from "@/helpers/formatBytes"
import { FileDownloadSource, FileDownloadStatusesBySource, FileMetaData } from "@/models"
import { Actions, Getters } from "@/store"
import { formatDate, formatDateAgo } from "@/utils/dateUtil"
import { openFilePreview } from "@/utils/openFilePreview"

@Options({
  name: "FileItemSimplified",
  components: {
    FileDownloadPic,
    FilePic,
    Avatar,
    DropdownTooltip,
    ProgressLoadingLine,
    Spinner
  }
})
export default class FileItemSimplified extends Vue {
  @Prop({
    required: true
  })
  file: FileMetaData
  @Prop({
    default: []
  })
  files: FileMetaData[]
  @Prop({
    default: false
  })
  removeButton
  @Prop({
    default: 0
  })
  progress: number
  @Prop({
    default: false
  })
  preventShow: boolean
  @Prop({
    default: ""
  })
  fancyboxGroup: string
  @Prop({
    default: "element",
    validator: (type: string) => ["element", "line"].includes(type)
  })
  fileMarkup: string

  @Action(Actions.ATTACHMENT_DOWNLOAD) downloadFile: ({
    fileMetadataId,
    fileName,
    source
  }: {
    fileMetadataId: string
    fileName: string
    source: FileDownloadSource
  }) => void
  @Action(Actions.ATTACHMENT_VIEW) attachmentView: (payload: {
    fileMetadataId: string
    fullSize?: boolean
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_DOCUMENT_PREVIEW) attachmentDocumentPreview: (payload: {
    fileMetadataId: string
  }) => Promise<Blob>
  @Getter(Getters.GET_CURRENT_PROJECT_ID) projectId: string
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean
  @Getter(Getters.GET_DOWNLOAD_STATUSES) downloadStatuses: Record<
    string,
    FileDownloadStatusesBySource
  >

  moreOptions = [
    {
      id: "delete",
      label: "Delete",
      icon: require("@icon/trash.svg"),
      iconOption: {
        width: "1.5em",
        height: "1.5em"
      }
    }
  ]

  get fileOwner() {
    return this.file.owner
  }

  get isLineType() {
    return this.fileMarkup === "line"
  }

  get isElType() {
    return this.fileMarkup === "element"
  }

  get timeAgo() {
    return formatDateAgo({
      date: new Date(),
      dateFormat: this.file.createdDateParsed,
      short: true
    })
  }

  get fileCreateDate() {
    return formatDate(this.file.createdDateParsed, "MM/dd/yy h:mm bb")
  }

  get filteredFiles() {
    return this.files.filter(file => file.isImage)
  }

  get jumpToIndex() {
    const index = this.filteredFiles.findIndex(file => file.id === this.file.id)

    return index < 0 ? 0 : index
  }

  get isBrowserPreviewable() {
    return Boolean(getBrowserPreviewMimeType(this.file.name))
  }

  get isPdfConversionPreviewable() {
    return isPdfConversionPreviewable(this.file.name)
  }

  get isPreviewable() {
    return this.isBrowserPreviewable || this.isPdfConversionPreviewable
  }

  get isNotOpenable() {
    return checkNotOpenable(this.file.name)
  }

  get downloadStatus() {
    return this.downloadStatuses[this.file.id]?.simplified
  }

  get fileSize() {
    return this.file.size === 0 ? "0 Bytes" : this.formatBytes(this.file.size.toString())
  }

  formatBytes(value: string) {
    return formatBytes(value)
  }

  onFileDownload() {
    const { id, name }: { id: string; name: string } = this.file

    this.downloadFile({
      fileMetadataId: id,
      fileName: name,
      source: "simplified"
    })
  }

  @Emit("deleteFile")
  deleteFile(hide) {
    hide()
    return this.file.id
  }

  async showImage() {
    if (this.preventShow) return
    if (this.isPdfConversionPreviewable) {
      await this.openConvertedDocumentPreview()
      return
    }
    if (this.isBrowserPreviewable) {
      await this.openBrowserPreview()
      return
    }
    if (!this.file.isImage) return

    if (!this.fancyboxGroup.length) {
      this.$fancybox.show([
        {
          previewBlob: this.attachmentView,
          fileMetadataId: this.file.id,
          loadFullSize: true,
          type: "image",
          caption: this.file.name
        }
      ])
      return
    }

    this.$fancybox
      .show(
        this.filteredFiles.map(file => ({
          previewBlob: this.attachmentView,
          fileMetadataId: file.id,
          loadFullSize: true,
          type: "image",
          caption: file.name
        }))
      )
      .jumpTo(this.jumpToIndex)
  }

  async openBrowserPreview() {
    await openFilePreview(this.file.name, () =>
      this.attachmentView({ fileMetadataId: this.file.id, fullSize: true })
    )
  }

  async openConvertedDocumentPreview() {
    await openFilePreview(
      this.file.name,
      () => this.attachmentDocumentPreview({ fileMetadataId: this.file.id }),
      PDF_PREVIEW_MIME_TYPE
    )
  }

  beforeUnmount() {
    this.$fancybox.unbind()
  }
}
</script>
